import {Component, HostListener, OnInit, Renderer2, ChangeDetectionStrategy} from '@angular/core';
import {Router} from '@angular/router';
import {Actions, ofActionDispatched, ofActionSuccessful, Select, Store} from '@ngxs/store';
import {Auth} from './actions/auth.actions';
import {AuthState} from './state/auth.state';
import {combineLatest, Observable} from 'rxjs';
import {NavigationGroup, NavigationItems} from './navigation.items';
import {map} from 'rxjs/operators';
import {OrganizationActions} from './actions/organiztion.actions';
import {Role} from './models/users/user.model';
import {OverlayContainer} from '@angular/cdk/overlay';
import {Settings} from './actions/settings.actions';
import {SettingsState} from './state/settings.state';
import {MatSlideToggleChange} from '@angular/material/slide-toggle';
import {SwUpdate, VersionEvent} from '@angular/service-worker';
import {MatSnackBar, MatSnackBarConfig} from '@angular/material/snack-bar';
import Logout = Auth.Logout;
import Login = Auth.Login;
import GetOrganizationModules = Auth.GetOrganizationModules;
import GetOrganization = OrganizationActions.GetOrganization;
import UpdateDarkMode = Settings.UpdateDarkMode;
import {environment} from '../environments/environment';
import {HttpClient} from '@angular/common/http';

@Component({
  standalone: false,
  selector: 'app-root',
  templateUrl: './app.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit {

  @Select(AuthState.isAuthenticated)
  public authenticated$: Observable<boolean>;

  @Select(AuthState.username)
  public loggedInUsername$: Observable<string>;

  @Select(AuthState.modules)
  public modules$: Observable<string[]>;

  @Select(AuthState.organization)
  public organizationHandle$: Observable<string>;

  @Select(SettingsState.darkMode)
  public darkMode$: Observable<boolean>;

  public sideNavOpened: boolean;
  public notificationNavOpened: boolean;
  public navigationGroups: Array<NavigationGroup>;
  public navigationGroupsLoading: boolean;
  private tokenExpiryTimer: any;

  constructor(
    private store: Store,
    private actions: Actions,
    private router: Router,
    private overlay: OverlayContainer,
    private renderer: Renderer2,
    private swUpdate: SwUpdate,
    private snackbar: MatSnackBar,
    private httpClient: HttpClient
  ) {
    this.sideNavOpened = true;
    this.notificationNavOpened = false;
    this.navigationGroups = [];
    this.navigationGroupsLoading = true;
  }


  ngOnInit(): void {
    this.darkMode$.subscribe((darkMode) => {
      const darkClassName = 'dark-theme';
      if (darkMode) {
        this.overlay.getContainerElement().classList.add(darkClassName);
        this.renderer.addClass(document.body, darkClassName);
        this.renderer.addClass(document.querySelector('html'), darkClassName);
      } else {
        this.overlay.getContainerElement().classList.remove(darkClassName);
        this.renderer.removeClass(document.body, darkClassName);
        this.renderer.removeClass(document.querySelector('html'), darkClassName);

      }
    });

    this.actions.pipe(ofActionDispatched(Logout)).subscribe(async () => {
      if (this.tokenExpiryTimer) {
        clearTimeout(this.tokenExpiryTimer);
        this.tokenExpiryTimer = null;
      }
      // Clear the state of other stores
      setTimeout(async () => {
        this.store.dispatch(new Auth.ClearState());
        await this.router.navigate(['/login']);
      }, 100);
    });

    this.actions.pipe(ofActionSuccessful(Login)).subscribe(async () => {
      this.store.dispatch([
        new GetOrganizationModules(),
        new GetOrganization(this.store.selectSnapshot(AuthState.organization)),
      ]);
    });

    this.authenticated$.subscribe(authenticated => {
      if (!this.tokenExpiryTimer && authenticated) {
        this.removeTokenAfterExpiry(this.store.selectSnapshot(AuthState.expires));
      }
    });

    this.modules$.subscribe((modules => {
      if ((modules as any).errorMessage) {
        this.store.dispatch(new Auth.ClearState()).subscribe(async () => {
          await this.router.navigate(['/error']);
        });
      } else if (modules.length === 0 && this.store.selectSnapshot(AuthState.isAuthenticated)) {
        this.navigationGroupsLoading = true;
        if (this.navigationGroups.length === 0) {
          this.store.dispatch([
            new GetOrganizationModules(),
            new GetOrganization(this.store.selectSnapshot(AuthState.organization))
          ]);
        }
      } else {
        this.navigationGroups = [];
        const roles = this.store.selectSnapshot(AuthState.roles);
        modules
          .filter(module => module !== 'REPORTING_MODULE')
          .forEach(module => {
              const navigationGroup: NavigationGroup = NavigationItems[module];
              if (navigationGroup) {
                if (this.hasRole(navigationGroup.role, roles)) {
                  const updatedNavigationItem: NavigationGroup = {
                    ...navigationGroup,
                    items: navigationGroup.items.filter(navigationItem => {
                      if (navigationItem.role && navigationItem.role.length > 0) {
                        return this.hasRole(navigationItem.role, roles);
                      }
                      return true;
                    })
                  };
                  this.navigationGroups.push(updatedNavigationItem);
                }
              }
            }
          );
        this.navigationGroupsLoading = false;
      }
    }));

    this.swUpdate.versionUpdates.subscribe((value: VersionEvent) => {
      console.log(value);
      if (value.type === 'VERSION_READY') {
        const config: MatSnackBarConfig = {
          duration: undefined,
          politeness: 'assertive',
          panelClass: 'errorSnackbar'
        };
        const snackbarRef = this.snackbar.open('New version available', 'UPDATE', config);

        snackbarRef.onAction().subscribe(() => {
          this.swUpdate.activateUpdate().then(() => location.reload());
          snackbarRef.dismiss();
        });
      }
    });
  }

  getNavigationGroups(): Array<NavigationGroup> {
    return this.navigationGroups.sort((a, b) => a.order - b.order);
  }

  showToolbar(): Observable<boolean> {
    return combineLatest([this.authenticated$, this.modules$]).pipe(map(value => {
      return value[0] && value[1].length > 0;
    }));
  }

  logout(): void {
    this.store.dispatch(new Logout());
  }

  toggleSideNav(): void {
    this.sideNavOpened = !this.sideNavOpened;
  }

  toggleNotificationNav(): void {
    this.notificationNavOpened = !this.notificationNavOpened;
  }

  removeTokenAfterExpiry(expires: Date): void {
    const now = new Date().getTime();
    const diff = new Date(expires).getTime() - now;
    console.log('Token expires in ' + diff + 'ms');
    this.tokenExpiryTimer = setTimeout(() => {
      this.store.dispatch(new Logout());
    }, diff);
  }

  hasRole(requiredRoles: Role[], principalRoles: string[]): boolean {
    return principalRoles.map(role => requiredRoles.includes(role as Role)).reduce((agg, val) => agg || val, false);
  }

  updateDarkMode($event: MatSlideToggleChange): void {
    this.store.dispatch(new UpdateDarkMode($event.checked));
  }

  getDarkModeValue(): boolean {
    return this.store.selectSnapshot(SettingsState.darkMode);
  }

  @HostListener('document:keydown.control.shift.f')
  search(event?: KeyboardEvent): void {
    this.router.navigate(['/', 'pos', 'search']);
  }

  getLogoUrl(): Observable<string> {
    return this.darkMode$.pipe(map(darkMode => darkMode ? 'assets/invflow_logo.png' : 'assets/invflow_logo.png'));
  }
}
